import { FormikErrors } from 'formik';
import { useEffect } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useNamespacesQuery } from '@/react/kubernetes/namespaces/queries/useNamespacesQuery';
import { useNamespaceAuthorizationChecker } from '@/react/hooks/useUser';
import { Authorization } from '@/react/portainer/rbac/authorizations';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect } from '@@/form-components/PortainerSelect';

export function filterNamespaces(
  namespaces: { IsSystem: boolean; Name: string }[] | undefined
) {
  return Object.values(namespaces ?? {})
    .filter((ns) => !ns.IsSystem)
    .map((ns) => ({
      label: ns.Name,
      value: ns.Name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

type Props = {
  onChange: (value: string) => void;
  values: string;
  errors: FormikErrors<string>;
  isEdit: boolean;
  /** When set, only namespaces where the user holds this authorization are offered. */
  requiredAuthorization?: Authorization;
};

export function NamespaceSelector({
  values: value,
  onChange,
  errors,
  isEdit,
  requiredAuthorization,
}: Props) {
  const environmentId = useEnvironmentId();
  const { data: namespaces, ...namespacesQuery } =
    useNamespacesQuery(environmentId);
  const namespaceNames = filterNamespaces(namespaces);

  const { canAccess, isLoading: accessLoading } =
    useNamespaceAuthorizationChecker(requiredAuthorization ?? []);
  const visibleNamespaces = requiredAuthorization
    ? namespaceNames.filter((ns) => canAccess(ns.value))
    : namespaceNames;

  const isLoading =
    namespacesQuery.isLoading || (!!requiredAuthorization && accessLoading);
  const firstNamespace = visibleNamespaces[0]?.value;

  useEffect(() => {
    if (!isEdit && !isLoading && !value && firstNamespace) {
      onChange(firstNamespace);
    }
  }, [isEdit, isLoading, value, firstNamespace, onChange]);

  return (
    <FormControl
      label="Namespace"
      inputId="namespace-selector"
      isLoading={isLoading}
      errors={errors}
    >
      {visibleNamespaces.length > 0 && (
        <PortainerSelect
          value={value}
          options={visibleNamespaces}
          onChange={onChange}
          disabled={isEdit}
          noOptionsMessage={() => 'No namespaces found'}
          placeholder="No namespaces found" // will only show when there are no options
          inputId="namespace-selector"
          data-cy="k8sAppCreate-nsSelect"
        />
      )}
    </FormControl>
  );
}
